import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import {
  getWaContacts, updateWaContact, deleteWaContact, addWaContactTags
} from '../../../services/api';
import { toast } from 'sonner';
import {
  MessageCircle, Search, X, Users, Trash2, Edit2, Tag, StickyNote, Settings,
  ChevronLeft, ChevronRight, Phone, User, ExternalLink,
  Inbox, Bot, FileText, Zap, Bell, Plus
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Cuentas', path: '/superadmin/whatsapp', icon: MessageCircle },
  { label: 'Inbox', path: '/superadmin/whatsapp/inbox', icon: Inbox },
  { label: 'Auto-respuestas', path: '/superadmin/whatsapp/auto-replies', icon: Bot },
  { label: 'Plantillas', path: '/superadmin/whatsapp/templates', icon: FileText },
  { label: 'Resp. Rapidas', path: '/superadmin/whatsapp/quick-replies', icon: Zap },
  { label: 'Contactos', path: '/superadmin/whatsapp/contacts', icon: Users },
  { label: 'Eventos', path: '/superadmin/whatsapp/event-settings', icon: Settings },
  { label: 'Notificaciones', path: '/superadmin/whatsapp/notifications', icon: Bell },
];

const WhatsAppContacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50 });

  // Detail modal
  const [selected, setSelected] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editName, setEditName] = useState('');
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (tagFilter) params.tag = tagFilter;
      const res = await getWaContacts(params);
      setContacts(res.data?.contacts || []);
      setPagination(res.data?.pagination || { total: 0, page: 1, limit: 50 });
    } catch { toast.error('Error cargando contactos'); }
    finally { setLoading(false); }
  }, [page, search, tagFilter]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  // Collect all unique tags for filter
  const allTags = [...new Set(contacts.flatMap(c => c.tags || []))].sort();

  const openDetail = (contact) => {
    setSelected(contact);
    setEditNotes(contact.notes || '');
    setEditName(contact.name || '');
    setNewTag('');
  };

  const saveContact = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await updateWaContact(selected.id, { name: editName, notes: editNotes });
      const updated = res.data?.contact;
      if (updated) {
        setSelected(updated);
        setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
      }
      toast.success('Contacto actualizado');
    } catch { toast.error('Error guardando'); }
    finally { setSaving(false); }
  };

  const handleAddTag = async () => {
    if (!selected || !newTag.trim()) return;
    try {
      const res = await addWaContactTags(selected.id, { tags: [newTag.trim()] });
      const updated = res.data?.contact;
      if (updated) {
        setSelected(updated);
        setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
      }
      setNewTag('');
    } catch { toast.error('Error agregando tag'); }
  };

  const handleRemoveTag = async (tag) => {
    if (!selected) return;
    const updatedTags = (selected.tags || []).filter(t => t !== tag);
    try {
      const res = await updateWaContact(selected.id, { tags: updatedTags });
      const updated = res.data?.contact;
      if (updated) {
        setSelected(updated);
        setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
      }
    } catch { toast.error('Error removiendo tag'); }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Eliminar contacto ${contact.name}?`)) return;
    try {
      await deleteWaContact(contact.id);
      toast.success('Contacto eliminado');
      setContacts(prev => prev.filter(c => c.id !== contact.id));
      if (selected?.id === contact.id) setSelected(null);
    } catch { toast.error('Error eliminando'); }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Sub-nav */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-slate-200 mb-4">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = item.path === '/superadmin/whatsapp/contacts';
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  active ? 'bg-green-100 text-green-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-green-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Contactos WhatsApp</h1>
              <p className="text-sm text-slate-500">{pagination.total} contactos</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por nombre o telefono..."
              className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500" />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {allTags.length > 0 && (
            <select value={tagFilter} onChange={e => { setTagFilter(e.target.value); setPage(1); }}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-600 focus:ring-2 focus:ring-green-500">
              <option value="">Todos los tags</option>
              {allTags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Cargando...</div>
          ) : contacts.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p className="font-medium">Sin contactos</p>
              <p className="text-sm mt-1">Los contactos se crean automaticamente al recibir mensajes</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Nombre</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Telefono</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Tags</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Mensajes</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Ultimo contacto</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(contact => (
                    <tr key={contact.id} className="border-b border-slate-50 hover:bg-slate-25 cursor-pointer" onClick={() => openDetail(contact)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{contact.name}</p>
                            {contact.is_blocked && (
                              <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded">bloqueado</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{contact.phone}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(contact.tags || []).slice(0, 3).map(tag => (
                            <span key={tag} className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{tag}</span>
                          ))}
                          {(contact.tags || []).length > 3 && (
                            <span className="text-[10px] text-slate-400">+{contact.tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">{contact.message_count || 0}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {contact.last_seen ? new Date(contact.last_seen).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => navigate(`/superadmin/whatsapp/inbox?search=${encodeURIComponent(contact.phone)}`)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="Ir a conversacion">
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button onClick={() => openDetail(contact)}
                            className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg" title="Editar">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(contact)}
                            className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" title="Eliminar">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                Mostrando {((page - 1) * pagination.limit) + 1}-{Math.min(page * pagination.limit, pagination.total)} de {pagination.total}
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-600 px-2">Pag {page} de {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contact detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Detalle de contacto</h3>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Avatar & Phone */}
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="w-8 h-8 text-green-700" />
                </div>
                <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                  <Phone className="w-3.5 h-3.5" /> {selected.phone}
                </p>
                <p className="text-xs text-slate-400 mt-1">{selected.message_count || 0} mensajes | Primer contacto: {selected.first_seen ? new Date(selected.first_seen).toLocaleDateString('es-MX') : '-'}</p>
              </div>

              {/* Name */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Nombre</label>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>

              {/* Tags */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <Tag className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-xs font-medium text-slate-600">Tags</span>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {(selected.tags || []).map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {(selected.tags || []).length === 0 && <span className="text-xs text-slate-400">Sin tags</span>}
                </div>
                <div className="flex gap-1">
                  <input value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddTag()}
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5" placeholder="Agregar tag" />
                  <button onClick={handleAddTag} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <StickyNote className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-xs font-medium text-slate-600">Notas</span>
                </div>
                <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={4}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" placeholder="Notas sobre el contacto..." />
              </div>

              {/* Custom fields */}
              {selected.custom_fields && Object.keys(selected.custom_fields).length > 0 && (
                <div>
                  <span className="text-xs font-medium text-slate-600 block mb-2">Campos personalizados</span>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                    {Object.entries(selected.custom_fields).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-sm">
                        <span className="text-slate-500">{k}</span>
                        <span className="text-slate-800 font-medium">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Conversations */}
              {selected.conversation_ids && selected.conversation_ids.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-slate-600 block mb-2">Conversaciones ({selected.conversation_ids.length})</span>
                  <button onClick={() => navigate(`/superadmin/whatsapp/inbox?search=${encodeURIComponent(selected.phone)}`)}
                    className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1">
                    <ExternalLink className="w-3.5 h-3.5" /> Ir al inbox
                  </button>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setSelected(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={saveContact} disabled={saving}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default WhatsAppContacts;
